import { useEffect } from "react";

export default function LightboxModal({ images = [], currentIndex = 0, isOpen = false, onClose, onNavigate }) {
  useEffect(() => {
    function handleKeyDown(e) {
      if (!isOpen) return;
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") {
        onNavigate((currentIndex - 1 + images.length) % images.length);
      }
      if (e.key === "ArrowRight") {
        onNavigate((currentIndex + 1) % images.length);
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, currentIndex, images.length, onClose, onNavigate]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen || images.length === 0) return null;

  const currentItem = images[currentIndex];
  const imageUrl = currentItem?.url || currentItem?.image?.[0] || currentItem?.src || "";
  const title = currentItem?.title || currentItem?.alt || "Projektbillede";
  const desc = currentItem?.description || "";

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex flex-col justify-between p-4 sm:p-6 animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-label="Billedvisning"
      onClick={onClose}
    >
      {/* Top bar with count & close */}
      <div className="flex items-center justify-between text-stone-200 max-w-7xl mx-auto w-full pt-2">
        <div className="text-xs sm:text-sm font-medium tracking-wide">
          Billede {currentIndex + 1} af {images.length}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-2 rounded-full bg-stone-800/80 hover:bg-stone-700 text-stone-200 hover:text-white transition-colors cursor-pointer"
          aria-label="Luk visning"
        >
          <i className="fa-solid fa-xmark text-lg sm:text-xl w-6 h-6 flex items-center justify-center" aria-hidden="true"></i>
        </button>
      </div>

      {/* Main Image View */}
      <div
        className="relative flex-1 flex items-center justify-center my-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Prev button */}
        {images.length > 1 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate((currentIndex - 1 + images.length) % images.length);
            }}
            className="absolute left-2 sm:left-6 z-10 w-11 h-11 rounded-full bg-stone-900/80 hover:bg-[#ce0611] text-white flex items-center justify-center transition-colors border border-stone-700/60 shadow-lg cursor-pointer"
            aria-label="Forrige billede"
          >
            <i className="fa-solid fa-chevron-left text-base" aria-hidden="true"></i>
          </button>
        )}

        <img
          src={imageUrl}
          alt={title}
          className="max-h-[75vh] max-w-[90vw] object-contain rounded-lg shadow-2xl border border-stone-800"
        />

        {/* Next button */}
        {images.length > 1 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate((currentIndex + 1) % images.length);
            }}
            className="absolute right-2 sm:right-6 z-10 w-11 h-11 rounded-full bg-stone-900/80 hover:bg-[#ce0611] text-white flex items-center justify-center transition-colors border border-stone-700/60 shadow-lg cursor-pointer"
            aria-label="Næste billede"
          >
            <i className="fa-solid fa-chevron-right text-base" aria-hidden="true"></i>
          </button>
        )}
      </div>

      {/* Bottom caption */}
      <div
        className="max-w-3xl mx-auto w-full text-center pb-2 px-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h4 className="text-white text-base sm:text-lg font-semibold">{title}</h4>
        {desc && <p className="text-stone-400 text-xs sm:text-sm mt-1">{desc}</p>}
      </div>
    </div>
  );
}
